import { useContext, useRef, useState } from "react";
import {
  PersonFiltersContext,
  PersonFiltersEventContext,
} from "../../../../context/FiltersContext";
import {
  QueryFilter,
  PersonFilterKeys,
  IPersonArtFilterSetting,
} from "../../../../interfaces/filters";
import {
  PersonartId,
  PersonDtoFilterInput,
} from "../../../../interfaces/schema";
import { f } from "../../../../util/f";
import { AccordionFilterItem } from "../../../accordion/Accordion";
import DateFilter from "../../../filter/date/DateFilter";
import InputCheckbox from "../../../formcontrols/InputCheckbox";
import { DateFilterEvent } from "../../../filter/date/types";
import { defaultDateEvent } from "../../../filter/date/dateFilterHelpers";

// helpers
const filterKey: QueryFilter<PersonFilterKeys>["filterKey"] = "personArt";

export default function PersonArtFilters() {
  // context
  const filters = useContext(PersonFiltersContext);
  const updateFilters = useContext(PersonFiltersEventContext);

  // refs
  const personartAdvokatRef = useRef<HTMLInputElement>(null);
  const personartFuldmaegtigRef = useRef<HTMLInputElement>(null);
  const personartOevrigRef = useRef<HTMLInputElement>(null);

  // filter settings
  const personArtSettings: IPersonArtFilterSetting = filters.find(
    (f) => f.filterKey === filterKey,
  )?.settings;

  // state
  const [latestDateEvent, setLatestDateEvent] =
    useState<DateFilterEvent>(defaultDateEvent);

  const isDateDisabled =
    personArtSettings?.artAdvokat ||
    personArtSettings?.artFuldmaegtig ||
    personArtSettings?.artOevrig
      ? false
      : true;

  // filter handler
  function updateFilterHandler(dateFilterEvent?: DateFilterEvent) {
    if (!dateFilterEvent) {
      dateFilterEvent = latestDateEvent;
    } else {
      setLatestDateEvent(dateFilterEvent);
    }

    const personartIds: PersonartId[] = [];

    if (personartAdvokatRef.current?.checked) {
      personartIds.push(PersonartId.Advokat);
    }

    if (personartFuldmaegtigRef.current?.checked) {
      personartIds.push(PersonartId.Advokatfuldmaegtig);
    }

    if (personartOevrigRef.current?.checked) {
      personartIds.push(PersonartId.Oevrig);
    }

    if (personartIds.length > 0) {
      // build query filter
      const queryFilter: QueryFilter<
        PersonFilterKeys,
        IPersonArtFilterSetting
      > = {
        filterKey: filterKey,
        filter: f<PersonDtoFilterInput>()
          .addFilter("personPersonart", {
            some: {
              and: [
                {
                  personartNr: {
                    in: personartIds,
                  },
                },
                {
                  periode: dateFilterEvent?.filter,
                },
              ],
            },
          })
          .build(),
        settings: {
          artAdvokat: personartAdvokatRef.current?.checked!,
          artFuldmaegtig: personartFuldmaegtigRef.current?.checked!,
          artOevrig: personartOevrigRef.current?.checked!,
          dateFilterSettings: dateFilterEvent?.settings,
        },
        parentFilter: "and",
      };

      updateFilters({
        event: "update",
        queryFilter: queryFilter,
      });
    } else {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
    }
  }

  return (
    <AccordionFilterItem
      title="Personart"
      value="personart"
      isActive={personArtSettings ? true : false}
    >
      <div className="l-flex-grid">
        <InputCheckbox
          label="Advokat"
          id="personartAdvokat"
          ref={personartAdvokatRef}
          checked={personArtSettings?.artAdvokat ?? false}
          onChange={() => {
            updateFilterHandler();
          }}
        />
        <InputCheckbox
          label="Fuldmægtig"
          id="personartFuldmaegtig"
          ref={personartFuldmaegtigRef}
          checked={personArtSettings?.artFuldmaegtig ?? false}
          onChange={() => {
            updateFilterHandler();
          }}
        />
        <InputCheckbox
          label="Øvrige"
          id="personartJurist"
          ref={personartOevrigRef}
          checked={personArtSettings?.artOevrig ?? false}
          onChange={() => {
            updateFilterHandler();
          }}
        />
      </div>
      <div className="mt-3">
        <DateFilter
          handleChange={(dateFilterEvent) => {
            updateFilterHandler(dateFilterEvent);
          }}
          settings={personArtSettings?.dateFilterSettings}
          disabled={isDateDisabled}
          key={JSON.stringify(personArtSettings?.dateFilterSettings)}
        />
      </div>
    </AccordionFilterItem>
  );
}
